import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import { CommonTooltip } from 'components/common/tooltip';
import { ShotRejectionHelper } from 'lib_ts/classes/shot-rejection.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  getTrackingDeviceCriteria,
  ShotCriteria,
} from 'lib_ts/enums/shot-rejection.enums';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';

interface IProps {
  shot: IMachineShot;
  pitch: IPitch;
  withSuffix?: boolean;
}

const FieldCell = ({
  label,
  highConfidence,
}: {
  label: string;
  highConfidence: boolean;
}) => (
  <td style={{ paddingRight: '8px' }} valign="top">
    <Flex align="center">
      {highConfidence ? (
        <CheckCircledIcon color={RADIX.COLOR.SUCCESS} />
      ) : (
        <CrossCircledIcon color={RADIX.COLOR.DANGER} />
      )}
      &nbsp;
      <Text size="1" weight="bold">
        {label}
      </Text>
    </Flex>
  </td>
);

export const MachineShotConfidenceBadge = ({
  shot,
  pitch,
  withSuffix,
}: IProps) => {
  if (!shot.confidence) {
    return <Badge color={RADIX.COLOR.SECONDARY}>N/A</Badge>;
  }

  const getConfidence = (trackingDevice?: TrackingDevice) => {
    if (!trackingDevice) {
      return {
        reject: true,
        reasons: ['No tracking device'],
      };
    }

    const criteria = getTrackingDeviceCriteria(trackingDevice);
    const { reject, reasons } = ShotRejectionHelper.filterShot({
      shot,
      criteria: criteria,
    });

    return {
      reject,
      reasons,
    };
  };

  const { reject, reasons } = getConfidence(shot.tracking_device);
  const confidenceText = reject ? 'Low' : 'High';

  return (
    <CommonTooltip
      side="left"
      trigger={
        <Badge
          color={reject ? RADIX.COLOR.DANGER : RADIX.COLOR.SUCCESS}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {confidenceText} {withSuffix ? ' Confidence' : ''}
        </Badge>
      }
      content={
        <Flex direction="column" gap="1" p="2">
          <Text weight="bold">
            {pitch.name} - {confidenceText} Confidence
          </Text>
          <Box>
            <hr />
          </Box>
          <Flex direction="column">
            <table style={{ borderSpacing: '0px 4px' }}>
              <tbody>
                <tr>
                  <FieldCell
                    label="Speed"
                    highConfidence={
                      !reasons.includes(ShotCriteria.ConfidenceSpeed)
                    }
                  />
                  <td>
                    <Text size="1">
                      {shot.confidence.PITCH_SpeedConfidence !== undefined
                        ? `SpeedConfidence: ${shot.confidence.PITCH_SpeedConfidence}`
                        : 'N/A'}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <FieldCell
                    label="Spin"
                    highConfidence={
                      !reasons.includes(ShotCriteria.ConfidenceSpin)
                    }
                  />
                  <td>
                    <Text size="1">
                      {`SpinConfidence: ${shot.confidence.PITCH_SpinConfidence ?? 'N/A'}`}
                    </Text>
                  </td>
                </tr>
                <tr>
                  <FieldCell
                    label="Plates"
                    highConfidence={
                      !reasons.includes(ShotCriteria.ConfidencePlates)
                    }
                  />
                  <td>
                    <Box>
                      <Text size="1">
                        {`StrikePositionHeightConfidence: ${shot.confidence.PITCH_StrikePositionHeightConfidence ?? 'N/A'}`}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="1">
                        {`StrikePositionSideConfidence: ${shot.confidence.PITCH_StrikePositionSideConfidence ?? 'N/A'}`}
                      </Text>
                    </Box>
                  </td>
                </tr>
                <tr>
                  <FieldCell
                    label="Breaks"
                    highConfidence={
                      !reasons.includes(ShotCriteria.ConfidenceBreak)
                    }
                  />
                  <td>
                    <Box>
                      <Text size="1">
                        {`VBTrajectoryConfidence: ${shot.break?.PITCH_VBTrajectoryConfidence ?? 'N/A'}`}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="1">
                        {`VBTrajectory: ${shot.break?.PITCH_VBTrajectory ?? 'N/A'}`}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="1">
                        {`HBTrajectoryConfidence: ${shot.break?.PITCH_HBTrajectoryConfidence ?? 'N/A'}`}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="1">
                        {`HBTrajectory: ${shot.break?.PITCH_HBTrajectory ?? 'N/A'}`}
                      </Text>
                    </Box>
                  </td>
                </tr>
              </tbody>
            </table>
          </Flex>
        </Flex>
      }
    />
  );
};
